<template>
  <slot
    :filters-open="filtersOpen"
    :toggle-filters="toggleFilters"
    :active-filters="activeFilters"
    :toggle-filter="toggleFilter"
    :is-filter-active="isFilterActive"
    :is-filter-available="isFilterAvailable"
    :is-filter-visible="isFilterVisible"
    :is-group-active="isGroupActive"
    :has-visible-pills="hasVisiblePills"
    :has-any-active-filter="hasAnyActiveFilter"
    :clear-filters="clearFilters"
    :filtered-count="filteredCount"
  />
</template>

<script>
export default {
  name: "CollectionFilter",
};
</script>

<script setup>
import {ref, reactive, computed, onMounted} from "vue";

const props = defineProps({
  totalCount: {
    type: Number,
    default: 0,
  },
  sizeValues: {
    type: String,
    default: "",
  },
  needValues: {
    type: String,
    default: "",
  },
  currentUrl: {
    type: String,
    default: "",
  },
});

const filtersOpen = ref(false);
const activeFilters = reactive({});
const filteredCount = ref(props.totalCount);
const availableFilters = reactive({});

const sizeValues = props.sizeValues
  ? props.sizeValues.split(",").map(s => s.trim()).filter(Boolean)
  : [];
const needValues = props.needValues
  ? props.needValues.split(",").map(s => s.trim()).filter(Boolean)
  : [];

const toggleFilters = () => {
  filtersOpen.value = !filtersOpen.value;
};

const isFilterActive = (group, value) => {
  if (!activeFilters[group]) return false;
  return activeFilters[group].includes(value);
};

const isGroupActive = group => {
  const active = activeFilters[group];
  return !!(active && active.length > 0);
};

const isFilterAvailable = (group, value) => {
  const key = `${group}:${value}`;
  return availableFilters[key] !== false;
};

// True when at least one filter is active across any group.
// Explicit per-group reads make reactivity tracking reliable on the
// initially-empty `reactive({})` object.
const hasAnyActiveFilter = computed(() => {
  const s = activeFilters.size;
  const n = activeFilters.need;
  return (s && s.length > 0) || (n && n.length > 0);
});

// Per-group visibility: hide ONLY siblings of the active pill within the
// SAME group. Other groups' pills stay fully visible so the user can
// combine filters across groups.
const isFilterVisible = (group, value) => {
  const active = activeFilters[group] || [];
  if (active.length === 0) return true;
  return active.includes(value);
};

// Whether a group has at least one pill that would actually render
// (matches the v-show condition used on pills in the template).
const hasVisiblePills = group => {
  const values = group === "size" ? sizeValues : needValues;
  return values.some(
    v =>
      isFilterVisible(group, v) &&
      (isFilterAvailable(group, v) || isFilterActive(group, v)),
  );
};

const broadcastFilterChange = () => {
  window.dispatchEvent(
    new CustomEvent("plp-filter-change", {
      detail: {
        size: (activeFilters.size && activeFilters.size[0]) || null,
        need: (activeFilters.need && activeFilters.need[0]) || null,
      },
    }),
  );
};

const toggleFilter = (group, value) => {
  // Single-select per group: clicking the same value clears it,
  // clicking a different value replaces the current selection.
  const current = activeFilters[group] || [];
  if (current.includes(value)) {
    activeFilters[group] = [];
  } else {
    activeFilters[group] = [value];
  }

  applyClientFilters();
  broadcastFilterChange();
};

const clearFilters = () => {
  Object.keys(activeFilters).forEach(key => {
    delete activeFilters[key];
  });
  applyClientFilters();
  broadcastFilterChange();
};

// Get all card elements and their tags
const getCards = () => {
  return Array.from(document.querySelectorAll(".plp-card[data-tags]")).map(
    card => ({
      el: card,
      tags: (card.getAttribute("data-tags") || "")
        .split(",")
        .map(t => t.trim().toLowerCase()),
    }),
  );
};

// Check if a card matches a specific filter group
const cardMatchesGroup = (tags, group, values) => {
  if (!values || values.length === 0) return true;
  return values.some(v => tags.includes(v.toLowerCase()));
};

const applyClientFilters = () => {
  const cards = getCards();
  const activeSizes = activeFilters.size || [];
  const activeNeeds = activeFilters.need || [];
  let visibleCount = 0;

  // Show/hide cards
  cards.forEach(({el, tags}) => {
    const matchesSize = cardMatchesGroup(tags, "size", activeSizes);
    const matchesNeed = cardMatchesGroup(tags, "need", activeNeeds);
    const show = matchesSize && matchesNeed;

    el.style.display = show ? "" : "none";
    if (show) visibleCount++;
  });

  filteredCount.value =
    activeSizes.length === 0 && activeNeeds.length === 0
      ? props.totalCount
      : visibleCount;

  // Update available filters based on current selection
  updateAvailableFilters(cards, activeSizes, activeNeeds);
};

const updateAvailableFilters = (cards, activeSizes, activeNeeds) => {
  // For each size value, check if selecting it (combined with current need filters) would show any products
  sizeValues.forEach(size => {
    const wouldMatch = cards.some(({tags}) => {
      const matchesSize = tags.includes(size.toLowerCase());
      const matchesNeed = cardMatchesGroup(tags, "need", activeNeeds);
      return matchesSize && matchesNeed;
    });
    availableFilters[`size:${size}`] = wouldMatch;
  });

  // For each need value, check if selecting it (combined with current size filters) would show any products
  needValues.forEach(need => {
    const wouldMatch = cards.some(({tags}) => {
      const matchesNeed = tags.includes(need.toLowerCase());
      const matchesSize = cardMatchesGroup(tags, "size", activeSizes);
      return matchesNeed && matchesSize;
    });
    availableFilters[`need:${need}`] = wouldMatch;
  });
};

onMounted(() => {
  // Initialize available filters
  const cards = getCards();
  updateAvailableFilters(cards, [], []);
});
</script>
